import React, { useState } from 'react';
import {
	Button,
	Card,
	CardContent,
	Typography,
	Box,
	Chip,
	Avatar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightRoundedIcon from '@mui/icons-material/CopyrightRounded';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { buildNewOrderFromQuotationUrl } from 'features/salesOrder/quotationImport/helpers/wizardUrls';
import LuggageIcon from '@mui/icons-material/Luggage';
import HotelIcon from '@mui/icons-material/Hotel';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import { QuotationsListItem } from 'features/quotation/types';
import { formatDestinationAndNights } from '../../helpers';
import NoFlightsPicture from '../../../../assets/no-flights.png';
import { QuotationListItemRoomDistribution } from '../QuotationListItemRoomDistribution';
import DealOrCaseOpenDetail from '../../../common/components/DealOrCaseOpenDetail';

const useStyles = makeStyles((theme) => ({
	card: {
		borderRadius: theme.spacing(2),
		boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
		border: '1px solid rgba(0,0,0,0.05)',
		transition: 'all 0.3s ease',
		overflow: 'hidden',
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
		},
		marginBottom: theme.spacing(2),
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			marginBottom: theme.spacing(1.5),
			boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
			position: 'relative', // Para posicionar el chip móvil
			'&:hover': {
				transform: 'none', // Quitar animación en móvil
				boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
			},
		},
	},
	cardContent: {
		padding: theme.spacing(3),
		'&:last-child': {
			paddingBottom: theme.spacing(3),
		},
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			padding: theme.spacing(2),
			'&:last-child': {
				paddingBottom: theme.spacing(2),
			},
		},
	},
	// Header con información principal del cliente
	clientHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: '#f8f9fa',
		borderRadius: theme.spacing(1),
		border: '1px solid #e9ecef',
	},
	clientInfo: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
	},
	clientAvatar: {
		backgroundColor: theme.palette.primary.main,
		width: 48,
		height: 48,
		fontSize: '1.2rem',
		fontWeight: 'bold',
	},
	clientDetails: {
		display: 'flex',
		flexDirection: 'column',
	},
	clientName: {
		fontWeight: 'bold',
		fontSize: '1.1rem',
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(0.5),
	},
	clientContact: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
		// Estilos móvil - mantener en líneas separadas pero compactas
		[theme.breakpoints.down('md')]: {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: theme.spacing(0.25),
		},
	},
	contactItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		cursor: 'pointer',
		padding: theme.spacing(0.25, 0.5),
		borderRadius: theme.spacing(0.5),
		transition: 'background-color 0.2s ease',
		// Solo en móvil hacer clickeable
		[theme.breakpoints.down('md')]: {
			'&:hover': {
				backgroundColor: 'rgba(0,0,0,0.04)',
			},
			'&:active': {
				backgroundColor: 'rgba(0,0,0,0.08)',
			},
		},
		// En desktop no clickeable
		[theme.breakpoints.up('md')]: {
			cursor: 'default',
			'&:hover': {
				backgroundColor: 'transparent',
			},
		},
	},
	contactSeparator: {
		// Ocultar separador en móvil
		[theme.breakpoints.down('md')]: {
			display: 'none',
		},
	},
	statusChips: {
		display: 'flex',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
		// Ocultar en móvil, se mostrará el chip móvil en su lugar
		[theme.breakpoints.down('md')]: {
			display: 'none',
		},
	},
	// Chip móvil posicionado en esquina superior derecha (circulito)
	mobileStatusChip: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1),
		minWidth: 20,
		width: 20,
		height: 20,
		padding: 0,
		borderRadius: '50%',
		display: 'none',
		'& .MuiChip-icon': {
			fontSize: '12px',
			margin: 0,
		},
		'& .MuiChip-label': {
			display: 'none', // Ocultar texto, solo icono
		},
		// Solo mostrar en móvil
		[theme.breakpoints.down('md')]: {
			display: 'flex',
		},
	},
	statusChip: {
		fontWeight: 'bold',
		textTransform: 'uppercase',
		fontSize: '0.75rem',
	},
	copiedChip: {
		backgroundColor: '#ff5722',
		color: 'white',
	},
	originalChip: {
		backgroundColor: '#4caf50',
		color: 'white',
	},

	// Información principal de viaje (destacada)
	travelMainInfo: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
			gap: theme.spacing(1.5),
			marginBottom: theme.spacing(1.5),
		},
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: '1fr 1fr', // 2 columnas en móvil pequeño
			gap: theme.spacing(1),
		},
	},
	travelInfoCard: {
		padding: theme.spacing(2),
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.primary.light}`,
		backgroundColor: theme.palette.primary.main + '08',
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.primary.main + '15',
		},
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			padding: theme.spacing(1.5),
			'&:hover': {
				borderColor: theme.palette.primary.light, // Menos hover en móvil
				backgroundColor: theme.palette.primary.main + '08',
			},
		},
	},
	travelInfoTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		color: theme.palette.primary.main,
		fontWeight: 'bold',
		fontSize: '0.875rem',
		marginBottom: theme.spacing(1),
		textTransform: 'uppercase',
	},
	travelInfoValue: {
		fontWeight: 'bold',
		fontSize: '1rem',
		color: theme.palette.text.primary,
	},
	travelInfoSecondary: {
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
		marginTop: theme.spacing(0.5),
	},

	// Información secundaria (compacta)
	secondaryInfo: {
		display: 'flex',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		flexWrap: 'wrap',
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			gap: theme.spacing(0.5),
			marginBottom: theme.spacing(1.5),
		},
	},
	quickInfoButton: {
		padding: theme.spacing(1, 2),
		border: `1px solid ${theme.palette.primary.light}`,
		borderRadius: theme.spacing(3),
		backgroundColor: 'white',
		color: theme.palette.primary.main,
		textTransform: 'none',
		fontSize: '0.875rem',
		fontWeight: 'normal',
		minHeight: 'auto',
		'&:hover': {
			backgroundColor: theme.palette.primary.main,
			color: 'white',
			borderColor: theme.palette.primary.main,
		},
		transition: 'all 0.2s ease',
	},

	// Sección de metadatos
	metaInfo: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: theme.spacing(2),
		borderTop: `1px solid ${theme.palette.primary.light}`,
		marginTop: theme.spacing(2),
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			flexDirection: 'column',
			alignItems: 'stretch',
			gap: theme.spacing(1.5),
			paddingTop: theme.spacing(1.5),
			marginTop: theme.spacing(1.5),
		},
	},
	metaLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: theme.spacing(1),
			fontSize: '0.8rem',
		},
		[theme.breakpoints.down('sm')]: {
			gap: theme.spacing(0.5),
		},
	},
	metaItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		// Hacer el link más fácil de clickear en móvil
		[theme.breakpoints.down('md')]: {
			'& a': {
				padding: theme.spacing(0.5),
				borderRadius: theme.spacing(0.5),
				textDecoration: 'underline',
				fontWeight: 'bold',
				'&:hover': {
					backgroundColor: 'rgba(0,0,0,0.04)',
				},
			},
		},
	},

	// Estilo especial para la fecha de inicio de viaje
	travelStartDate: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
		backgroundColor: theme.palette.primary.main + '15',
		padding: theme.spacing(0.5, 1),
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.primary.main + '30'}`,
		color: theme.palette.primary.main,
		fontWeight: 'bold',
	},

	// Botones de acción
	actionButtons: {
		display: 'flex',
		gap: theme.spacing(1),
		// Estilos móvil - mantener en la misma línea con igual height
		[theme.breakpoints.down('md')]: {
			flexDirection: 'row',
			gap: theme.spacing(0.5),
			width: '100%',
			'& button': {
				flex: 1, // Igual ancho
				height: 40, // Altura fija
				minHeight: 40,
			},
		},
	},
	primaryButton: {
		backgroundColor: theme.palette.primary.main,
		color: 'white',
		fontWeight: 'bold',
		padding: theme.spacing(1, 2),
		borderRadius: theme.spacing(3),
		textTransform: 'none',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
			transform: 'translateY(-1px)',
			boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
		},
		transition: 'all 0.3s ease',
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			padding: theme.spacing(0.5, 1.5),
			fontSize: '0.875rem',
			height: 40,
			minHeight: 40,
			'&:hover': {
				transform: 'none',
				boxShadow: 'none',
			},
		},
	},
	secondaryButton: {
		background: 'transparent',
		color: theme.palette.primary.main,
		border: `1px solid ${theme.palette.primary.main}`,
		fontWeight: 'bold',
		padding: theme.spacing(1, 2),
		borderRadius: theme.spacing(3),
		textTransform: 'none',
		'&:hover': {
			backgroundColor: theme.palette.primary.main,
			color: 'white',
			transform: 'translateY(-1px)',
		},
		transition: 'all 0.3s ease',
		// Estilos móvil
		[theme.breakpoints.down('md')]: {
			padding: theme.spacing(0.5, 1.5),
			fontSize: '0.875rem',
			height: 40,
			minHeight: 40,
			'&:hover': {
				backgroundColor: theme.palette.primary.main,
				color: 'white',
				transform: 'none',
			},
		},
	},

	// Estilos para modales
	modalContent: {
		padding: theme.spacing(2),
	},
	flightRoute: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(2),
		padding: theme.spacing(1.5),
		backgroundColor: '#f5f5f5',
		borderRadius: theme.spacing(1),
		'&:last-child': {
			marginBottom: 0,
		},
	},
	airlineInfo: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		minWidth: '120px',
	},
	carrierLogo: {
		width: 40,
		height: 40,
		borderRadius: theme.spacing(1),
		objectFit: 'contain',
		backgroundColor: 'white',
		padding: theme.spacing(0.5),
		border: '1px solid #eee',
	},
	flightDetails: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		flex: 1,
		justifyContent: 'center',
	},
	airportInfo: {
		textAlign: 'center',
		minWidth: '80px',
	},
	airportCode: {
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		fontSize: '1.1rem',
	},
	flightDate: {
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
	},
	arrowIcon: {
		color: theme.palette.primary.main,
		fontSize: '2rem',
	},
	priceSection: {
		textAlign: 'right',
		minWidth: '140px',
	},
	priceMain: {
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		fontSize: '1.25rem',
	},
	priceSecondary: {
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
	},
	servicesInfo: {
		display: 'flex',
		gap: theme.spacing(1),
		marginTop: theme.spacing(2),
		flexWrap: 'wrap',
	},
	serviceChip: {
		fontSize: '0.75rem',
		height: '24px',
	},
	includesService: {
		backgroundColor: '#e8f5e8',
		color: '#2e7d32',
	},
	excludesService: {
		backgroundColor: '#ffebee',
		color: '#c62828',
	},
	noFlightsContainer: {
		textAlign: 'center',
		padding: theme.spacing(3),
		backgroundColor: '#f5f5f5',
		borderRadius: theme.spacing(1),
		color: theme.palette.text.secondary,
		border: '2px dashed #ccc',
	},
	noFlightsImage: {
		width: '60px',
		height: '60px',
		opacity: 0.5,
		marginBottom: theme.spacing(1),
	},
}));

interface Props {
	data: QuotationsListItem;
	onClick: () => void;
	/** URL de edición de la cotización: habilita ctrl/cmd+click y clic del medio. */
	href: string;
	onDuplicate: () => void;
}

function QuotationListItem(props: Props): JSX.Element {
	const classes = useStyles();
	const [flightModalOpen, setFlightModalOpen] = useState(false);
	const [roomModalOpen, setRoomModalOpen] = useState(false);

	// Función para copiar al portapapeles
	const copyToClipboard = (text: string, type: string) => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				// Aquí podrías agregar un toast o notificación si lo deseas
				console.log(`${type} copiado al portapapeles: ${text}`);
			})
			.catch((err) => {
				console.error('Error al copiar al portapapeles:', err);
			});
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-ES', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
		}).format(amount);
	};

	// Función para formatear fecha en formato DD/MM/AAAA HH:MM:SS
	const formatDateTime = (dateString: string) => {
		try {
			const date = new Date(dateString);
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const year = date.getFullYear();
			const hours = date.getHours().toString().padStart(2, '0');
			const minutes = date.getMinutes().toString().padStart(2, '0');
			const seconds = date.getSeconds().toString().padStart(2, '0');

			return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
		} catch (error) {
			return dateString; // Fallback en caso de error
		}
	};

	// Función para formatear solo fecha en formato DD/MM/AAAA
	const formatDate = (dateString: string) => {
		try {
			// Para fechas en formato YYYY-MM-DD, extraer directamente sin timezone
			if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
				const [year, month, day] = dateString.split('-');
				return `${day}/${month}/${year}`;
			}

			// Fallback para otros formatos (compatibilidad hacia atrás)
			const date = new Date(dateString);
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const year = date.getFullYear();

			return `${day}/${month}/${year}`;
		} catch (error) {
			return dateString; // Fallback en caso de error
		}
	};

	// Obtener las fechas del primer vuelo para mostrar las fechas de viaje
	const getTravelDates = () => {
		if (!props.data.air?.data || props.data.air.data.length === 0) {
			return 'Fechas no disponibles';
		}

		const firstFlight = props.data.air.data[0];
		const lastFlight = props.data.air.data[props.data.air.data.length - 1];

		if (props.data.air.data.length === 1) {
			return formatDate(firstFlight.departure.date);
		}

		return `${formatDate(firstFlight.departure.date)} - ${formatDate(lastFlight.arrival.date)}`;
	};

	// Obtener fecha de inicio de viaje usando departureDate del backend
	const getTravelStartDate = () => {
		// Usar directamente el departureDate que viene del backend
		if (props.data.departureDate) {
			return formatDate(props.data.departureDate);
		}

		// Si el backend no lo puede calcular, nosotros tampoco deberíamos intentarlo
		return 'No disponible';
	};

	// Función anterior comentada por si se necesita volver atrás
	// const getTravelStartDate = () => {
	// 	// Primero intentar obtener del primer vuelo si existe
	// 	if (props.data.air?.data && props.data.air.data.length > 0) {
	// 		return formatDate(props.data.air.data[0].departure.date);
	// 	}
	// 	// Si no hay vuelos, retornar "No disponible"
	// 	return 'No disponible';
	// };

	// Calcular total de noches
	const getTotalNights = () => {
		return props.data.destinationNamesAndNights.reduce((total, dest) => total + dest.nights, 0);
	};

	// Calcular total de pasajeros
	const getTotalPassengers = () => {
		if (!props.data.roomDistributions) return 'No especificado';

		const total = props.data.roomDistributions.reduce(
			(sum, room) => sum + room.totalAdults + room.totalChildren + room.totalInfants,
			0,
		);

		return `${total} pasajero${total !== 1 ? 's' : ''}`;
	};

	// Obtener iniciales del nombre
	const getClientInitials = () => {
		return props.data.name
			.split(' ')
			.map((word) => word.charAt(0))
			.join('')
			.substring(0, 2)
			.toUpperCase();
	};

	return (
		<Card className={classes.card}>
			{/* Chip móvil en esquina superior derecha */}
			<Chip
				size="small"
				className={`${classes.mobileStatusChip} ${classes.statusChip} ${
					props.data.wasCopied ? classes.copiedChip : classes.originalChip
				}`}
				icon={<CopyrightRoundedIcon fontSize="small" />}
			/>
			<CardContent className={classes.cardContent}>
				{/* Header del Cliente */}
				<Box className={classes.clientHeader}>
					<Box className={classes.clientInfo}>
						<Avatar className={classes.clientAvatar}>{getClientInitials()}</Avatar>
						<Box className={classes.clientDetails}>
							<Typography className={classes.clientName}>{props.data.name}</Typography>
							<Box className={classes.clientContact}>
								<Box
									className={classes.contactItem}
									onClick={() => copyToClipboard(props.data.phone.toString(), 'Teléfono')}
								>
									<PhoneIcon fontSize="small" />
									<Typography variant="body2">{props.data.phone}</Typography>
								</Box>
								{props.data.email && (
									<>
										<Typography variant="body2" className={classes.contactSeparator}>
											•
										</Typography>
										<Box className={classes.contactItem} onClick={() => copyToClipboard(props.data.email, 'Email')}>
											<EmailIcon fontSize="small" />
											<Typography variant="body2">{props.data.email}</Typography>
										</Box>
									</>
								)}
							</Box>
						</Box>
					</Box>

					<Box className={classes.statusChips}>
						<Chip
							size="small"
							className={`${classes.statusChip} ${props.data.wasCopied ? classes.copiedChip : classes.originalChip}`}
							label={props.data.wasCopied ? 'Copia' : 'Original'}
							icon={<CopyrightRoundedIcon />}
						/>
						{props.data.isOnlyFlights && (
							<Chip size="small" label="Solo Vuelos" icon={<FlightTakeoffIcon />} color="primary" variant="outlined" />
						)}
					</Box>
				</Box>

				{/* Información Principal de Viaje */}
				<Box className={classes.travelMainInfo}>
					{/* Destinos */}
					<Box className={classes.travelInfoCard}>
						<Typography className={classes.travelInfoTitle}>
							<LocationOnIcon fontSize="small" />
							Destinos
						</Typography>
						<Typography className={classes.travelInfoValue}>
							{formatDestinationAndNights(props.data.destinationNamesAndNights)}
						</Typography>
						<Typography className={classes.travelInfoSecondary}>
							{props.data.destinationNamesAndNights.length} destino
							{props.data.destinationNamesAndNights.length !== 1 ? 's' : ''}
						</Typography>
					</Box>

					{/* Fechas y Noches */}
					<Box className={classes.travelInfoCard}>
						<Typography className={classes.travelInfoTitle}>
							<NightsStayIcon fontSize="small" />
							Duración
						</Typography>
						<Typography className={classes.travelInfoValue}>
							{getTotalNights()} noche{getTotalNights() !== 1 ? 's' : ''}
						</Typography>
						<Typography className={classes.travelInfoSecondary}>{getTravelDates()}</Typography>
					</Box>

					{/* Pasajeros */}
					{props.data.roomDistributions && (
						<Box className={classes.travelInfoCard}>
							<Typography className={classes.travelInfoTitle}>
								<FamilyRestroomIcon fontSize="small" />
								Pasajeros
							</Typography>
							<Typography className={classes.travelInfoValue}>{getTotalPassengers()}</Typography>
							<Typography className={classes.travelInfoSecondary}>
								{props.data.roomDistributions.length} habitación{props.data.roomDistributions.length !== 1 ? 'es' : ''}
							</Typography>
						</Box>
					)}

					{/* Precio de Vuelos */}
					{props.data.air && (
						<Box className={classes.travelInfoCard}>
							<Typography className={classes.travelInfoTitle}>
								<AttachMoneyIcon fontSize="small" />
								Precio Vuelos
							</Typography>
							<Typography className={classes.travelInfoValue}>{formatCurrency(props.data.air.fareADT)}</Typography>
							<Typography className={classes.travelInfoSecondary}>Por adulto</Typography>
						</Box>
					)}
				</Box>

				{/* Información Secundaria (Botones Compactos) */}
				<Box className={classes.secondaryInfo}>
					{props.data.air && (
						<Button
							className={classes.quickInfoButton}
							startIcon={<FlightTakeoffIcon fontSize="small" />}
							onClick={() => setFlightModalOpen(true)}
						>
							Ver Vuelos
						</Button>
					)}

					{props.data.roomDistributions && (
						<Button
							className={classes.quickInfoButton}
							startIcon={<HotelIcon fontSize="small" />}
							onClick={() => setRoomModalOpen(true)}
						>
							Ver Habitaciones
						</Button>
					)}
				</Box>

				{/* Información Meta y Acciones */}
				<Box className={classes.metaInfo}>
					<Box className={classes.metaLeft}>
						<Box className={classes.metaItem}>
							<BusinessIcon fontSize="small" />
							<Typography variant="body2">
								{props.data.isBitrixDeal ? 'Bitrix' : 'Caso'} #
								<DealOrCaseOpenDetail dealId={props.data.crmTicket} isBitrixDeal={props.data.isBitrixDeal} />
							</Typography>
						</Box>
						<Box className={classes.metaItem}>
							<PersonIcon fontSize="small" />
							<Typography variant="body2">{props.data.agentName}</Typography>
						</Box>
						<Box className={classes.travelStartDate}>
							<FlightTakeoffIcon fontSize="small" />
							<Typography variant="body2">Inicio: {getTravelStartDate()}</Typography>
						</Box>
						<Box className={classes.metaItem}>
							<CalendarTodayIcon fontSize="small" />
							<Typography variant="body2">{formatDateTime(props.data.created)}</Typography>
						</Box>
						<Typography variant="body2" color="textSecondary">
							ID: {props.data.id}
						</Typography>
					</Box>

					<Box className={classes.actionButtons}>
						<Button
							className={classes.secondaryButton}
							startIcon={<ContentCopyIcon />}
							onClick={props.onDuplicate}
							size="small"
						>
							Copiar
						</Button>
						<Button
							className={classes.secondaryButton}
							component="a"
							href={buildNewOrderFromQuotationUrl(props.data.id)}
							target="_blank"
							rel="noopener noreferrer"
							startIcon={<PostAddIcon />}
							size="small"
						>
							Crear orden
						</Button>
						{/* Link real: clic simple navega igual que antes, pero ctrl/cmd+click,
						    clic del medio y "abrir en pestaña nueva" funcionan nativamente. */}
						<Button
							className={classes.primaryButton}
							component="a"
							href={props.href}
							startIcon={<VisibilityIcon />}
							onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
								// Dejar que el navegador maneje los clics con modificadores.
								if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
								event.preventDefault();
								props.onClick();
							}}
							size="small"
						>
							Ver / Editar
						</Button>
					</Box>
				</Box>
			</CardContent>

			{/* Modal de Vuelos */}
			<Dialog open={flightModalOpen} onClose={() => setFlightModalOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>
					<Box display="flex" flexDirection="column" gap={1}>
						<Box display="flex" alignItems="center" gap={1}>
							<FlightTakeoffIcon />
							Información de Vuelos
						</Box>
						<Typography variant="body2" color="textSecondary" style={{ fontSize: '0.875rem' }}>
							Itinerario principal de la cotización:
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent className={classes.modalContent}>
					{props.data.air ? (
						<>
							{props.data.air.data?.map((leg, index) => (
								<Box key={index} className={classes.flightRoute}>
									<Box className={classes.airlineInfo}>
										<img className={classes.carrierLogo} src={leg.airline.image} alt={leg.airline.name} />
										<Box>
											<Typography variant="body2" fontWeight="bold">
												{leg.airline.code}
											</Typography>
											<Typography variant="caption" color="textSecondary">
												{leg.flightNumber}
											</Typography>
										</Box>
									</Box>

									<Box className={classes.flightDetails}>
										<Box className={classes.airportInfo}>
											<Typography className={classes.airportCode}>{leg.departure.airport.code}</Typography>
											<Typography className={classes.flightDate}>{formatDate(leg.departure.date)}</Typography>
											<Typography variant="caption" color="textSecondary">
												{leg.departure.hour}
											</Typography>
										</Box>

										<ArrowRightAltIcon className={classes.arrowIcon} />

										<Box className={classes.airportInfo}>
											<Typography className={classes.airportCode}>{leg.arrival.airport.code}</Typography>
											<Typography className={classes.flightDate}>{formatDate(leg.arrival.date)}</Typography>
											<Typography variant="caption" color="textSecondary">
												{leg.arrival.hour}
											</Typography>
										</Box>
									</Box>
								</Box>
							))}

							{/* Sección de Precios Total */}
							<Box
								className={classes.priceSection}
								style={{
									marginTop: '16px',
									marginBottom: '16px',
									textAlign: 'right',
								}}
							>
								<Typography className={classes.priceMain}>{formatCurrency(props.data.air?.fareADT || 0)}</Typography>
								<Typography className={classes.priceSecondary}>Precio Total - Adulto</Typography>
								{(props.data.air?.fareCHD || 0) > 0 && (
									<Typography className={classes.priceSecondary}>
										Niño: {formatCurrency(props.data.air?.fareCHD || 0)}
									</Typography>
								)}
								{(props.data.air?.fareINF || 0) > 0 && (
									<Typography className={classes.priceSecondary}>
										Inf: {formatCurrency(props.data.air?.fareINF || 0)}
									</Typography>
								)}
							</Box>

							<Box className={classes.servicesInfo}>
								<Chip
									size="small"
									className={`${classes.serviceChip} ${
										props.data.air?.carryOn ? classes.includesService : classes.excludesService
									}`}
									icon={props.data.air?.carryOn ? <CheckIcon /> : <CloseIcon />}
									label="Equipaje de mano"
								/>
								<Chip
									size="small"
									className={`${classes.serviceChip} ${
										props.data.air?.baggage !== 'none' ? classes.includesService : classes.excludesService
									}`}
									icon={props.data.air?.baggage !== 'none' ? <LuggageIcon /> : <CloseIcon />}
									label={`Equipaje: ${props.data.air?.baggage !== 'none' ? props.data.air?.baggage : 'No incluido'}`}
								/>
							</Box>

							{props.data.air.images?.length > 0 && (
								<Box mt={2}>
									<img
										src={props.data.air.images[0].image}
										alt="Imagen del vuelo"
										style={{
											width: '100%',
											height: '200px',
											objectFit: 'cover',
											borderRadius: '8px',
											border: '1px solid #eee',
										}}
									/>
								</Box>
							)}
						</>
					) : (
						<Box className={classes.noFlightsContainer}>
							<img className={classes.noFlightsImage} src={NoFlightsPicture} alt="Sin vuelos" />
							<Typography variant="h6" gutterBottom>
								Sin vuelos configurados
							</Typography>
							<Typography variant="body2" color="textSecondary">
								Esta cotización no incluye información de vuelos
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setFlightModalOpen(false)}>Cerrar</Button>
				</DialogActions>
			</Dialog>

			{/* Modal de Habitaciones */}
			<Dialog open={roomModalOpen} onClose={() => setRoomModalOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Box display="flex" alignItems="center" gap={1}>
						<HotelIcon />
						Distribución de Habitaciones
					</Box>
				</DialogTitle>
				<DialogContent className={classes.modalContent}>
					{props.data.roomDistributions && (
						<QuotationListItemRoomDistribution
							roomDistributions={props.data.roomDistributions}
							quotationId={props.data.id}
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRoomModalOpen(false)}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
}

export default QuotationListItem;
